import { Injectable } from '@angular/core';
import { Account } from '@wb/core/interfaces/account';
import { Admin } from '@wb/core/interfaces/admin';
import { Query } from 'apollo-angular';
import gql from 'graphql-tag';

export interface AccountsResponse {
  accounts: {
    code: string;
    success: boolean;
    message: string;
    accounts: Account[];
  };
}

export interface AccountResponse {
  account: {
    code: string;
    success: boolean;
    message: string;
    account: Account;
  };
}

export interface AccountQueryVariables {
  limit?: number;
  offset?: number;
  where?: AccountFilter;
  orderBy?: string[];
}
export interface AccountFilter {
  uid?: string;
  uids?: string[];
  account?: string;
  address?: string;
  city?: string;
  state?: string;
  postalcode?: string;
  county?: string;
  country?: string;
  dealer_logo?: string;
  dealer_logo_key?: string;
  integrations?: string;
  integration_status?: string;
  configuration?: string;
  demo?: string;
  oem_type?: string;
  pricing?: string;
  landing_page?: string;
  atc_quote_type?: string;
  loan_provider?: string;
  routeone_id?: string;
  routeone_status?: string;
  dealertrack_id?: string;
  dealertrack_status?: string;
  cdk_last_import?: string;
  cdk_last_hit?: string;
  created_at?: string;
  updated_at?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccountsGQL extends Query<AccountsResponse, AccountQueryVariables> {
  document = gql(`
    query accounts ($where: ModelAccountFilterInput, $limit: Int, $offset: Int, $orderBy: [String]) {
      accounts (where: $where, limit: $limit, offset: $offset, orderBy: $orderBy) {
        code
        success
        message
        accounts {
          uid
          account
          city
          county
          state
          oem_type
          superAdmin
          address
          city
          state
          postalcode
          concierge {
            name
            phone
          }
          group {
            uid
            name
            default_account_id
          }
          childAccounts{
            uid
            account
            city
            state
            phone
            domain
          }
          settings {
            key
            value
          }
        }
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class AccountPricingGQL extends Query<AccountResponse, { uid: string }> {
  document = gql(`
    query account($uid: ID!) {
      account(uid: $uid) {
        code
        success
        message
        account {
          pricing {
            type
            amount
            max
            apr_markup
            base_price
            payoff_days
            type_used
            amount_used
            max_used
            used_base_price
            apr_markup_lease
          }
        }
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class AccountGQL extends Query<AccountResponse> {
  document = gql(`
    query account ($uid: ID!) {
      account (uid: $uid) {
        success
        message
        code
        account {
          uid
          account
          address
          city
          state
          postalcode
          county
          country
          dealer_logo
          phone
          pricing {
            type
            amount
            max
            apr_markup
            base_price
            payoff_days
            type_used
            amount_used
            max_used
            used_base_price
            apr_markup_lease
          }
          atc_quote_type
          oem_type
          settings {
            key
            value
          }
          hours {
            open
            close
            day
          }
          group {
            name
            uid
          }
          concierge {
            uid
            name
            phone
            email
          }
        }
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class AccountSettingsGQL extends Query<AccountResponse, { uid: string }> {
  document = gql(`
    query account ($uid: ID!) {
      account (uid: $uid) {
        success
        message
        code
        account {
          uid
          account
          settings {
            key
            value
          }
        }
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class AccountBasicsAndSettingsGQL extends Query<AccountResponse, { uid: string }> {
  document = gql(`
    query account ($uid: ID!) {
      account (uid: $uid) {
        success
        message
        code
        account {
          uid
          account
          address
          city
          state
          postalcode
          county
          country
          dealer_logo
          phone
          pricing {
            type
            amount
            max
            apr_markup
            base_price
            payoff_days
            type_used
            amount_used
            max_used
            used_base_price
            apr_markup_lease
          }
          atc_quote_type
          oem_type
          settings {
            key
            value
          }
        }
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class AccountImportProfilesGQL extends Query<AccountResponse, { uid: string }> {
  document = gql(`
    query accountImportProfiles ($uid: ID!) {
      account (uid: $uid) {
        success
        message
        code
        account {
          uid
          account
          importProfiles {
            uid
            ftp_user
            type
            shared_profile_id
            importProfileUid
            importProfileName
            importProfileUploadsType
            importProfileUploadsCreatedAt
            importProfileUploadsProcessedAt
          }
        }
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class AccountBillingProfilesGQL extends Query<AccountResponse, { uid: string }> {
  document = gql(`
    query account ($uid: ID!) {
      account (uid: $uid) {
        success
        message
        code
        account {
          uid
          account
          settings {
            key
            value
          }
          billingProfiles {
            uid
            type
            ftp_user
            profileName
            profileUid
          }
        }
      }
    }
  `);
}

export interface AdminsResponse {
  admins: {
    code: string;
    success: boolean;
    message: string;
    admins: Admin[];
  };
}

export interface AdminFilter {
  uid?: string;
  accountUid?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  privilege?: number;
  settings?: string;
  breadcrumb?: string;
  image?: string;
  image_key?: string;
  status?: string;
  token?: string;
  last_login?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AccountAdminsQueryVariables {
  where?: AdminFilter;
  limit?: number;
  offset?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AccountAdminsGQL extends Query<AdminsResponse, AccountAdminsQueryVariables> {
  document = gql(`
    query admins ($where: ModelAdminFilterInput, $limit: Int, $offset: Int) {
      admins (where: $where, limit: $limit, offset: $offset) {
        code
        success
        message
        admins {
          uid
          first_name
          last_name
          email
          status
          accountUid
          role {
            name
          }
        }
      }
    }
  `);
}

export interface AccountThemeResponse {
  accountThemes: {
    code: string;
    message: string;
    success: boolean;
    buttonTheme?: string;
    clientTheme?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AccountIntegrationsGQL extends Query<AccountResponse> {
  document = gql(`
    query account ($uid: ID!) {
      account (uid: $uid) {
        code
        success
        message
        account {
          uid
          integrations
          loan_provider
          routeone_id
          dealertrack_id
          settings {
            key
            value
          }
          importProfiles {
            importProfileName
            importProfileUid
          }
        }
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class AccountThemesGQL extends Query<AccountThemeResponse, { uid: string }> {
  document = gql(`
    query accountThemes($uid: ID!) {
      accountThemes(uid: $uid) {
        code
        success
        message
        buttonTheme
        clientTheme
      }
    }
  `);
}

export interface RelatedAccount {
  uid: string;
  account: string;
  assertionId: boolean;
}

export interface RelatedAccountMap {
  uid: string;
  account: string;
  assertionId: string;
}

export interface AccountMap {
  uid: string;
  account: string;
  map?: string;
}

export interface AccountMapsResponse {
  account: {
    account: {
      accountMaps: AccountMap[];
      groupAccounts: RelatedAccount[];
      uniqueMaps: string[];
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class AccountMapsGQL extends Query<AccountMapsResponse, { uid: string }> {
  document = gql(`
    query account($uid: ID!) {
      account(uid: $uid) {
        code
        success
        message
        account {
          accountMaps {
            uid
            account
            map
          }
          groupAccounts {
            uid
            account
          }
          uniqueMaps
        }
      }
    }
  `);
}

export interface ChildMapsResponse {
  childAccountMaps: {
    accountMaps: AccountMap[];
    childAccounts: RelatedAccount[];
    uniqueMaps: string[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class ChildAccountMapsGQL extends Query<ChildMapsResponse, { uid: string }> {
  document = gql(`
    query childAccountMaps($uid: ID!) {
      childAccountMaps(uid: $uid) {
        code
        success
        message
        accountMaps {
          uid
          account
          map
        }
        childAccounts {
          uid
          account
          assertionId
        }
        uniqueMaps
      }
    }
  `);
}

export interface AggregatorAccountsResponse {
  aggregatorAccounts: {
    accountMaps: AccountMap[];
    aggregatorAccounts: RelatedAccount[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class AggregatorAccounts extends Query<AggregatorAccountsResponse, { uid: string }> {
  document = gql(`
    query aggregatorAccounts($uid: ID!) {
      aggregatorAccounts(uid: $uid) {
        code
        success
        message
        aggregatorAccounts {
          uid
          account
          assertionId
        }
      }
    }
  `);
}

export interface SalesPerson {
  uid: string;
  first_name: string;
  last_name: string;
}

export interface SalesPersonResponse {
  salesPersons: {
    admins: SalesPerson[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class AccountSalespeopleGQL extends Query<SalesPersonResponse, { accountUid: string }> {
  document = gql(`
    query salesPersons ($accountUid: ID!) {
      salesPersons (uid: $accountUid) {
        admins {
          uid
          first_name
          last_name
        }
      }
    }
  `);
}
